import { useMemo } from "react";
import { createFileRoute, type LinkProps } from "@tanstack/react-router";
import z from "zod";

import { useIntegrations } from "@/hooks/use-integrations";
import { formatShortDate } from "@/utils";
import { InfoField, JsonDisplay } from "../../-components";

const OpenAiPromptDetailPage = () => {
  const { id } = Route.useParams();

  const { data: promptResponse } = useIntegrations().getPromptResponse(id);

  type InfoFieldType = { label: string; value: string | number } & LinkProps;

  const infoFields: InfoFieldType[] = useMemo(() => {
    const fields: InfoFieldType[] = [];
    if (promptResponse) {
      fields.push({ label: "ID", value: promptResponse.id });
      fields.push({ label: "Prompt ID", value: promptResponse.promptId ?? "N/A" });
      fields.push({ label: "Prompt Version", value: promptResponse.promptVersion ?? "N/A" });
      fields.push({ label: "Model", value: promptResponse.model ?? "N/A" });
      fields.push({ label: "Successful", value: promptResponse.successful ? "Yes" : "No" });

      if (promptResponse.userId) {
        fields.push({
          label: "Profile ID",
          value: promptResponse.userId,
          to: "/profiles/$id/careplans",
          params: { id: promptResponse.userId },
        });
      }

      fields.push({
        label: "Created At",
        value: formatShortDate(promptResponse.createdAt),
      });
    }

    return fields;
  }, [promptResponse]);

  if (!promptResponse) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4 rounded-xl bg-surface p-4">
        {infoFields.map((fields) => {
          return <InfoField key={fields.label} {...fields} />;
        })}
      </div>

      {promptResponse.input || promptResponse.output ? (
        <div className="grid grid-cols-2 gap-4 rounded-xl bg-surface p-4">
          {promptResponse.input ? <JsonDisplay data={promptResponse.input} label="Input" /> : null}
          {promptResponse.output ? (
            <JsonDisplay data={promptResponse.output} label="Output" />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export const Route = createFileRoute("/_private/integrations/open-ai/prompts/$id/")({
  component: OpenAiPromptDetailPage,
  params: {
    parse: (params: Record<string, string>) => {
      return { id: z.number().int().parse(Number(params.id)) };
    },
  },
});
